// 🟦 React & Hooks
import { useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';

// 🟦 Validators & Helpers
import { validateCardInfo } from './helpers';
import { generatePassangersArr } from '../AssistReserve/helpers';
import { postMetaConverter } from '@shared/services/meta';

import { useTheme, useMediaQuery, Box } from '@mui/material';

// 🟦 Services
import { postAssistEmit, postAssistPayment } from '@shared/services/services';

// 🟦 UI Components
import PaymentsSteps from './PaymentsSteps';
import PaymentMethodSelector from '@shared/components/PaymentMethodSelector';
import PayeeAndConfirm from './PayeeAndConfirm';
import BillingData from '@shared/components/BillingData';
import CoverageDetails from '../CoverageDetails';
import GenericBookingLayout from '@shared/components/GenericBookingLayout';
import BoxContainer from '@shared/components/BoxContainer';
import { InfoDialogModal } from '@shared/components/Dialogs';

import {
  ASSIST_PAYMENTS_SUCCESSFUL,
  ASSIST_SEARCH,
} from '@shared/helpers/routes';

import { getUser } from '@shared/helpers/auth';
import { useBillingError } from '@shared/hooks/useBillingError';
import billingBookingSchema from '@shared/components/BillingData/billingBookingSchema';
import { usePayeeSelection } from '@shared/components/PayeeSelector/usePayeeSelection';
import PriceDetails from '../PriceDetails';

const AssistPayments = () => {
  const location = useLocation();
  const history = useHistory();
  const user = useSelector(store => store.userInfo);
  const actualUser = getUser();

  const state = location.state;

  const contact = state?.contact ? state?.contact : null;
  const precios = state?.storagePassengers.price.details;

  const [infoCard, setInfoCard] = useState(null);
  const [cardData, setCardData] = useState();
  const [wasPaid, setWasPaid] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [isLoadingPay, setIsLoadingPay] = useState(false);
  const payForm = '0';

  const [errorEmit, setErrorEmit] = useState(false);

  const {
    billingError,
    showBillingError,
    clearBillingError,
  } = useBillingError();

  const [count, setCount] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const payee = usePayeeSelection();

  const formikPaymentRef = useRef(null);
  const paymentSelectorRef = useRef(null);

  const handleChangeInfoCard = data => {
    setInfoCard(data);
  };

  const returnFormValidator = (isMobile, isPointOfSale) =>
    billingBookingSchema({
      isMobile,
      withEntity: true,
      isPointOfSale,
    });

  const assistBodyEmit = async (newIdTransaction, salesResult, id_entity) => {
    let passengersArrBody = generatePassangersArr(
      state?.passengers,
      state?.noViaja,
      state?.contact?.email
    );
    let xTransactionId = state?.xTransactionId;
    let newBody = {
      product_code: state?.storagePassengers?.codigo,
      rate_code: state?.storagePassengers?.codigoTarifa,
      start_date: state?.storagePassengers?.start_date,
      end_date: state?.storagePassengers?.end_date,
      destination: state?.storagePassengers?.destination,
      passengers: passengersArrBody,
      id_transaction: newIdTransaction,
      id_user: actualUser ? actualUser.user.id : null,

      id_assigned_seller: payee.seller?.id_user ?? null,
      id_entity,
      client_email: contact.email,
      agency_email: payee.subsidiary?.email,
      comments: '-',
    };
    try {
      const resultado = await postAssistEmit(newBody, xTransactionId);

      globalThis.dataLayer?.push({
        event: 'compraEmisionAssist',
        eventProps: {
          category: 'compra',
          action: 'submit',
          label: 'confirmar',
          value: {
            datos_pasajeros: state?.passengers,
            email_voucher: contact?.email,
            datos_noViaja: state?.noViaja,
            agencia: payee.subsidiary?.name,
            precios,
            datos_planAssist: {
              product_code: state?.storagePassengers?.codigo,
              rate_code: state?.storagePassengers?.codigoTarifa,
              start_date: state?.storagePassengers?.start_date,
              end_date: state?.storagePassengers?.end_date,
              destination: state?.storagePassengers?.destination,
              passengers: passengersArrBody,
              xTransactionId,
              id_transaction: newIdTransaction,
            },
          },
        },
      });

      postMetaConverter(
        {
          event_name: 'compraEmisionAssist',
          custom_data: {
            category: 'compra',
            action: 'submit',
            label: 'confirmar',
            datos_pasajeros: state?.passengers,
            email_voucher: contact?.email,
            datos_noViaja: state?.noViaja,
            agencia: payee.subsidiary?.name,
            precios,
            datos_planAssist_product_code: state?.storagePassengers?.codigo,
            datos_planAssist_rate_code: state?.storagePassengers?.codigoTarifa,
            datos_planAssist_start_date: state?.storagePassengers?.start_date,
            datos_planAssist_end_date: state?.storagePassengers?.end_date,
            datos_planAssist_destination: state?.storagePassengers?.destination,
            datos_planAssist_passengers: passengersArrBody,
            datos_planAssist_xTransactionId: xTransactionId,
            datos_planAssist_id_transaction: newIdTransaction,
          },
        },
        user
      );
      history.push({
        pathname: ASSIST_PAYMENTS_SUCCESSFUL,
        state: {
          cardData,
          resultado,
          salesResult,
          state,
          subsidiary: payee.subsidiary,
          seller: payee.seller,
        },
      });
    } catch (error) {
      console.error('error', error);

      const code = error.response?.data?.code;

      if (code === 'ISSUANCE_ALREADY_DONE') {
        history.push({
          pathname: ASSIST_PAYMENTS_SUCCESSFUL,
          state: {
            cardData,
            code,
            salesResult,
            state,
            subsidiary: payee.subsidiary,
            seller: payee.seller,
            isSuccessPayment: true,
          },
        });

        return;
      }

      setErrorEmit(true);
    } finally {
      setIsLoadingPay(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      ownerCardName: '',
      ownerCardLastName: '',
      ownerDocument: '',
      afip: '',
      street: '',
      streetNumber: '',
      floor: '',
      province: '',
      department: '',
      postalCode: '',
      city: '',
      id_entity: payee.subsidiary?.id || '',
      id_assigned_seller: payee.seller?.id_user || '',
    },
    validationSchema: returnFormValidator(isMobile, payee?.isPointOfSale),
    onSubmit: async values => {
      if (wasPaid) {
        setIsLoadingPay(true);
        setCount(1);
        assistBodyEmit(
          paymentResult.id_transaction,
          paymentResult,
          values.id_entity
        );
      } else {
        //-------------------PAGO TARJETA------------------------------------
        if (infoCard === null && +payForm !== 1) {
          showBillingError({
            title: 'Parece que faltan datos de tu tarjeta',
            message: 'Ingresalos para poder finalizar la compra.',
            subText: '',
          });

          return;
        }
        if (+payForm === 0) {
          if (infoCard === undefined || validateCardInfo(infoCard)) {
            showBillingError({
              title: 'Hubo un error en la carga de datos de la tarjeta.',
              message:
                'Por favor, verifique la información ingresada e intente nuevamente.',
              subText: '',
            });

            return;
          }

          const bodyPayment = {
            id_transaction: location?.state?.id_transaction,
            id_user: actualUser ? actualUser.user.id : null,
            payment_plan_id: infoCard.idPlan,
            agency_email: payee.subsidiary?.email,
            id_entity: values.id_entity,
            client_email: contact?.email,
            payment_object: {
              card_number: infoCard.cardInfo.number,
              card_exp_month: infoCard.cardInfo.card_exp_month,
              card_exp_year: infoCard.cardInfo.card_exp_year,
              card_code_value: infoCard.cardInfo.cvc,
              currency: 'ARS',
              card_holder_name: infoCard.cardInfo.name,
              card_holder_identification_type: 'dni',
              card_holder_identification_number: infoCard.cardInfo.dni,
              agency_name: payee.subsidiary?.name,
              agency_business_name: payee.subsidiary?.business_name,
              ref_passenger: '-',
              file_number: '-',
            },
            billing_data: {
              owner_card_name: values.ownerCardName,
              owner_card_last_name: values.ownerCardLastName,
              owner_document: values.ownerDocument,
              afip: values.afip,
              street: values.street,
              street_number: values.streetNumber,
              floor: values.floor,
              department: values.department,
              city: values.city,
              postal_code: values.postalCode,
              province: values.province,
            },
          };
          try {
            setIsLoadingPay(true);
            const result = await postAssistPayment(
              bodyPayment,
              location?.state?.xTransactionId
            );

            if (result?.payment_result?.status == 'APPROVED') {
              assistBodyEmit(result?.id_transaction, result, values.id_entity);
              setPaymentResult(result);
              setWasPaid(true);
            }
          } catch (error) {
            console.error('tarjeta error', error);
            showBillingError({
              title: 'El pago no pudo ser procesado',
              message:
                'Por favor, verificá los datos e intentá nuevamente. Si el inconveniente continúa, comunicate con tu punto de venta.',
              subText: '',
            });

            setIsLoadingPay(false);
          }
        }
      }
    },
  });

  return (
    <GenericBookingLayout
      pago={true}
      operationType="compra"
      steps={[
        { title: 'Assist Card', navigate: null, data: null },
        { title: 'Compra', navigate: null, data: null },
      ]}
      searchPath={ASSIST_SEARCH}
      storageKey={state.storageKey}
      stepsWrapperComponent={<PaymentsSteps />}
      formComponent={
        <>
          <PaymentMethodSelector
            handleChangeInfoCard={handleChangeInfoCard}
            precio={precios}
            setCardData={setCardData}
            cardData={cardData}
            idPlan={8}
            formikPaymentRef={formikPaymentRef}
            ref={paymentSelectorRef}
          />

          {+payForm === 0 && (
            <BillingData
              formik={formik}
              billingError={billingError}
              clearBillingError={clearBillingError}
              isMobile={isMobile}
            />
          )}

          <PayeeAndConfirm
            formik={formik}
            isLoadingPay={isLoadingPay}
            payee={payee}
            formikPaymentRef={formikPaymentRef}
            paymentSelectorRef={paymentSelectorRef}
          />
        </>
      }
      detailsComponent={
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <BoxContainer>
              <PriceDetails
                storagePassengers={location?.state?.storagePassengers}
                cardData={cardData}
                total={
                  location?.state?.storagePassengers?.price?.details?.total
                }
                totalUsd={
                  location.state.elementSelected?.price?.details_USD?.total
                }
                isPayment
                currencyValue={
                  location.state.elementSelected?.price?.currency_value
                }
                hasCharges={
                  !!location.state.elementSelected?.price?.details?.charges
                }
              />
            </BoxContainer>

            <BoxContainer>
              <CoverageDetails
                storagePassengers={location?.state?.storagePassengers}
                cardData={cardData}
              />
            </BoxContainer>
          </Box>

          {errorEmit && (
            <InfoDialogModal
              isOpen={errorEmit}
              onClose={() => {
                if (count === 0) {
                  setErrorEmit(false);
                } else {
                  history.push({
                    pathname: ASSIST_PAYMENTS_SUCCESSFUL,
                    state: {
                      cardData,
                      resultado: null,
                      state,
                      subsidiary: payee.subsidiary,
                      seller: payee.seller,
                    },
                  });
                }
              }}
              title={
                count === 0
                  ? 'Ocurrio un error al emitir la cobertura'
                  : 'La cobertura no se emitió correctamente'
              }
              text={
                count === 0
                  ? 'Por favor, intente nuevamente'
                  : 'El pago ha sido realizado con éxito, deberá comunicarse con la sucursal seleccionada para finalizar la emisión'
              }
            />
          )}
        </>
      }
    />
  );
};

export default AssistPayments;
